/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ClusterService } from '../../utilities';
import { ErrorResponseModel } from '../../../../../../core/models';
import { Injectable } from '@angular/core';
import { IpGroupModel } from '../../../models';
import { LogService, TransactionService, UtilsService, ValidationService } from '../../../../../../core/services';
import { NetworkingApiService } from '../../../http';
import { catchError, concatMap, mergeMap } from 'rxjs/operators';
import { from, Observable, of } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class NetworkingService {
    constructor(
        private clusterService: ClusterService,
        private logService: LogService,
        private networkingApiService: NetworkingApiService,
        private transactionService: TransactionService,
        private utilsService: UtilsService
    ) {
    }

    /**
     * Get local assigned interface
     * @param group
     * @param interfaces
     */
    public getLocalAssignedInterface(group: IpGroupModel, interfaces: any[]) {
        const localNodeId = this.clusterService.localNode?.id;
        const localInterface = group.assignedInterfaces.find(
            item => item.nodeId === localNodeId
        );

        return interfaces.find(
            item => item.id === localInterface?.interfaceId
        );
    }

    /**
     * Get the interfaces for the selected bond.
     */
    public getBondInterfaces(interfaces: any[], bondId: string): any[] {
        return interfaces.filter(item => item.type === 'bondChild' && item.settings.parentId === bondId);
    }

    /**
     * Sort DNS.
     * @param formDns
     */
    public sortDNS(formDns: any): any {
        const dnsIPv4: string[] = [];
        const dnsIPv6: string[] = [];
        for (const dns of formDns) {
            if (ValidationService.ipV4Check(dns)) {
                dnsIPv4.push(dns);
            } else {
                dnsIPv6.push(dns);
            }
        }

        return { dnsIPv4: this.utilsService.cleanArray(dnsIPv4), dnsIPv6: this.utilsService.cleanArray(dnsIPv6) };
    }

    /**
     * Commit transaction.
     */
    public commitTransaction(): Observable<any> {
        return new Observable<any>(observer => {
            this.transactionService.commitTransaction()
                .subscribe({
                    next: (): void => {
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel) => {
                        this.logService.error(
                            'NetworkingService: commitTransaction() error committing transaction ', error.message
                        );
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Submission failed.
     */
    public submitFailed(): void {
        this.rollbackTransaction()
            .subscribe({
                next: (): void => {
                    this.logService.error(
                        'NetworkingService() submitFailed', 'transaction successfully rolled back'
                    );
                },
                error: () => {
                    this.logService.error('NetworkingService() submitFailed', 'Rollback failed');
                }
            });
    }

    /**
     * Remove bond.
     * @param interfaces
     * @param bondId
     */
    public removeBond(interfaces: any[], bondId: string): Observable<any> {
        return new Observable<any>((observer) => {
            this.removeInterfacesFromBond(interfaces)
                .pipe(concatMap(() => this.deleteBondHelper(bondId)))
                .subscribe({
                    next: () => {
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Remove interfaces from the bond before breaking.
     */
    public removeInterfacesFromBond(interfaces: any[]): Observable<any> {
        return new Observable((observer) => {
            const interfaceLinks = from(interfaces || []);
            if (!interfaces.length) {
                observer.next();
                observer.complete();
            } else {
                interfaceLinks.pipe(
                    mergeMap(row => this.networkingApiService.removeInterfaceFromBond(row['id']).pipe(catchError(error => of(error)))),
                ).subscribe({
                    next: () => {
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                });
            }
        });
    }

    /**
     * Helper function for removing a bond.
     * @param bondId
     * @private
     */
    private deleteBondHelper(bondId: string): Observable<any> {
        return new Observable((observer) => {
            this.networkingApiService.removeBond(bondId)
                .subscribe({
                    next: () => {
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Rollback transaction.
     * @private
     */
    private rollbackTransaction(): Observable<any> {
        return new Observable<any>(observer => {
            this.transactionService.rollbackTransaction()
                .subscribe({
                    next: () => {
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }
}
